import { useRef, useCallback, useEffect, useState } from 'react';
import { Rnd } from 'react-rnd';
import { computeSnap, computeResizeSnap } from '../../utils/snapUtils';
import { normalizeWindowRect } from '../../utils/panelLayout';
import { PanelHeaderSlotContext } from './headerSlot';
import styles from './WindowFrame.module.css';

let zCounter = 100;

export default function WindowFrame({
  id,
  title,
  children,
  layout,
  onUpdate,
  onClose,
  onMaximize,
  maximized = false,
  maximizedContainerRef,
  dragBounds = 'window',
  allLayouts = {},
  minWidth  = 240,
  minHeight = 120,
}) {
  const zRef   = useRef(zCounter);
  const rndRef = useRef(null);
  const snapRef = useRef(null);
  // Title-bar slot that panel children portal their header controls into
  const [headerSlot, setHeaderSlot] = useState(null);

  useEffect(() => {
    const el = document.getElementById(`wf-${id}`);
    if (!el) return;
    const reset = () => { el.scrollTop = 0; el.scrollLeft = 0; };
    el.addEventListener('scroll', reset, { passive: true });
    return () => el.removeEventListener('scroll', reset);
  }, [id]);

  const bringToFront = useCallback(() => {
    zCounter += 1;
    zRef.current = zCounter;
    const el = document.getElementById(`wf-${id}`);
    if (el) el.style.zIndex = zCounter;
  }, [id]);

  // A window opened from the View menu should land on top of the stack, not
  // under previously-raised windows.
  useEffect(() => { bringToFront(); }, [bringToFront]);

  const getBoundaryRect = useCallback(() => {
    const boundary = typeof dragBounds === 'string'
      ? document.querySelector(dragBounds)
      : dragBounds;
    return boundary?.getBoundingClientRect?.() ?? null;
  }, [dragBounds]);

  const getBoundaryTop = useCallback(() => {
    const top = getBoundaryRect()?.top;
    return Number.isFinite(top) ? top : null;
  }, [getBoundaryRect]);

  // When maximized, position/size fill the mainArea container.
  const containerRect = maximized && maximizedContainerRef?.current
    ? maximizedContainerRef.current.getBoundingClientRect()
    : null;

  // `layout` is read straight from persisted (localStorage) state, which can
  // hold a stale/partial entry — e.g. a channel panel whose x/y were saved
  // without width/height, or a NaN from some earlier bug (JSON silently
  // serializes NaN as null). react-rnd's underlying re-resizable library
  // calls `.toString()` directly on a null/non-number size, crashing the
  // whole app ("Cannot read properties of null (reading 'toString')") — so
  // validate at this boundary rather than trust the stored value. Width is
  // also clamped here so a layout saved before a panel gained a larger minimum
  // cannot mount with its title-bar controls overlapping.
  const safeLayout = normalizeWindowRect(layout, minWidth, minHeight);
  const layoutNeedsRepair =
    safeLayout.x !== layout.x || safeLayout.y !== layout.y ||
    safeLayout.width !== layout.width || safeLayout.height !== layout.height;

  // Self-heal the persisted entry once, so the fix sticks across reloads
  // instead of re-triggering every mount.
  useEffect(() => {
    if (layoutNeedsRepair) onUpdate(id, safeLayout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutNeedsRepair]);

  // Bounds passed to react-rnd only constrain interactive dragging/resizing —
  // they don't touch the initial mounted position. A panel whose stored (or
  // default cascade) x/y falls outside the current workspace area — e.g. under
  // the lap-selector sidebar/header, or off the edge of a shrunk browser
  // window — would otherwise render with its title bar (the only drag handle)
  // unreachable, leaving it permanently stuck. Re-clamp on mount and whenever
  // the window resizes, persisting the correction like a normal drag would.
  useEffect(() => {
    if (containerRect) return undefined;
    const clamp = () => {
      const rect = getBoundaryRect();
      if (!rect) return;
      const maxX = Math.max(rect.left, rect.right - safeLayout.width);
      const maxY = Math.max(rect.top, rect.bottom - safeLayout.height);
      const x = Math.min(Math.max(safeLayout.x, rect.left), maxX);
      const y = Math.min(Math.max(safeLayout.y, rect.top), maxY);
      if (x !== safeLayout.x || y !== safeLayout.y) onUpdate(id, { x, y });
    };
    clamp();
    window.addEventListener('resize', clamp);
    return () => window.removeEventListener('resize', clamp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRect, getBoundaryRect, id, safeLayout.x, safeLayout.y, safeLayout.width, safeLayout.height, onUpdate]);

  const handleDrag = useCallback((_, d) => {
    const others = Object.entries(allLayouts)
      .filter(([k]) => k !== id)
      .map(([, v]) => v);
    const snapped = computeSnap(d.x, d.y, safeLayout.width, safeLayout.height, others);
    // Snapping runs after react-rnd applies its drag bounds. Clamp once more so
    // a legacy panel saved above the workspace cannot snap this panel back
    // underneath the application/session toolbars.
    const boundaryTop = getBoundaryTop();
    if (boundaryTop != null) snapped.y = Math.max(snapped.y, boundaryTop);
    snapRef.current = snapped;
    const draggable = rndRef.current?.draggable;
    if (draggable?.setState) {
      draggable.setState({ x: snapped.x, y: snapped.y });
    }
  }, [allLayouts, getBoundaryTop, id, safeLayout.width, safeLayout.height]);

  const handleDragStop = useCallback(() => {
    const pos = snapRef.current;
    snapRef.current = null;
    if (pos) onUpdate(id, pos);
  }, [id, onUpdate]);

  const handleResizeStop = useCallback((_, direction, ref, __, pos) => {
    const others = Object.entries(allLayouts)
      .filter(([k]) => k !== id)
      .map(([, v]) => v);
    const snapped = computeResizeSnap(
      pos.x, pos.y,
      parseInt(ref.style.width,  10),
      parseInt(ref.style.height, 10),
      direction,
      others,
    );
    const boundaryTop = getBoundaryTop();
    if (boundaryTop != null && snapped.y < boundaryTop) {
      const bottom = snapped.y + snapped.height;
      snapped.y = boundaryTop;
      snapped.height = Math.max(minHeight, bottom - boundaryTop);
    }
    onUpdate(id, snapped);
  }, [allLayouts, getBoundaryTop, id, minHeight, onUpdate]);

  const position = containerRect
    ? { x: containerRect.left, y: containerRect.top }
    : { x: safeLayout.x, y: safeLayout.y };

  const size = containerRect
    ? { width: containerRect.width, height: containerRect.height }
    : { width: safeLayout.width, height: safeLayout.height };

  return (
    <Rnd
      ref={rndRef}
      id={`wf-${id}`}
      position={position}
      size={size}
      minWidth={containerRect  ? 0 : minWidth}
      minHeight={containerRect ? 0 : minHeight}
      bounds={containerRect ? undefined : dragBounds}
      dragHandleClassName={containerRect ? undefined : styles.titleBar}
      // Header controls are *portaled* into the title bar, so they sit inside the
      // drag handle in the DOM even though they live elsewhere in the React tree —
      // which is why the slot's React `onMouseDown` stopPropagation below never
      // reaches them (portal events bubble through the React tree, not the DOM one).
      // react-draggable matches the handle against the real DOM ancestors, so a
      // mousedown anywhere in the slot starts a window drag. Firefox is where this
      // bites: it dispatches mousedown when you grab a scrollbar (Chrome doesn't),
      // so dragging the channel-picker list's scrollbar dragged the whole panel.
      cancel={`.${styles.headerSlot}`}
      disableDragging={!!containerRect}
      enableResizing={!containerRect}
      className={`${styles.window} ${containerRect ? styles.windowMaximized : ''}`}
      style={{ zIndex: containerRect ? 9999 : zRef.current }}
      onMouseDown={containerRect  ? undefined : bringToFront}
      onDrag={containerRect       ? undefined : handleDrag}
      onDragStop={containerRect   ? undefined : handleDragStop}
      onResizeStop={containerRect ? undefined : handleResizeStop}
    >
      <div
        className={`${styles.titleBar} ${containerRect ? styles.titleBarMaximized : ''}`}
        onDoubleClick={onMaximize}
      >
        <span className={styles.title}>{title}</span>
        <div
          className={styles.headerSlot}
          ref={setHeaderSlot}
          // Controls (buttons, inputs, dropdowns) must not start a window drag
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        />
        {onMaximize && (
          <button
            className={styles.maximizeBtn}
            onClick={onMaximize}
            onMouseDown={(e) => e.stopPropagation()}
            title={maximized ? 'Restore (Esc)' : 'Maximize'}
          >
            {maximized ? '⤡' : '⤢'}
          </button>
        )}
        {onClose && (
          <button className={styles.closeBtn} onClick={onClose} onMouseDown={(e) => e.stopPropagation()} title="Close">✕</button>
        )}
      </div>
      <div className={styles.content}>
        <PanelHeaderSlotContext.Provider value={headerSlot}>
          {children}
        </PanelHeaderSlotContext.Provider>
      </div>
    </Rnd>
  );
}
